<template>
  <div class="project-slider slider">
    <div class="project-slider__inner">
      <swiper-container
        ref="swiper"
        init="false"
        :slides-per-view="1"
        pagination
      >
        <swiper-slide
          v-for="(slide, index) in filtredCovers"
          :key="index"
          class="swiper-slide"
        >
          <VideoComponent
            v-if="slide.type === 'video'"
            :play="currentIndex === index && !showSliderModal"
            :muted="muteVideo && !showSliderModal"
            :poster="slide.videoPart?.preview"
            :src="slide.data"
          />
          <SafeImage
            v-else
            :src="slide.imagePart?.preview || slide.data"
            :fetchpriority="index === 0 ? 'high' : 'low'"
            :alt="`Slide ${index + 1}`"
            :loading="index === 0 ? 'eager' : 'lazy'"
          />
        </swiper-slide>
      </swiper-container>

      <div class="project-slider__controls slider-controls">
        <transition name="slideright">
          <div
            v-if="filtredCovers?.[currentIndex]?.type === 'video'"
            class="slider-controls__button"
            @click="muteVideo = !muteVideo"
          >
            <svgo-sound-off v-if="muteVideo" class="icon" />
            <svgo-sound-on v-else class="icon" />
          </div>
        </transition>

        <div
          class="slider-controls__button fullscreen"
          @click="showSliderModal = true"
        >
          <svgo-full class="icon" />
        </div>
      </div>

      <template v-if="filtredCovers.length > 1">
        <div
          class="button-prev"
          :class="{ '--disabled': currentIndex === 0 }"
          @click="goPrev"
        >
          <svgo-arrow-left width="24" height="24" class="icon" />
        </div>
        <div
          class="button-next"
          :class="{ '--disabled': currentIndex === filtredCovers.length - 1 }"
          @click="goNext"
        >
          <svgo-arrow-right width="24" height="24" class="icon" />
        </div>
      </template>

      <client-only>
        <div v-if="isMobile" class="project-slider__title-wrapper">
          <div v-if="finished" class="donate-finish">
            <svgo-finish-donate width="24px" height="24px" class="icon" />
            <p>{{ $t('donate.completed') }}</p>
          </div>

          <p class="project-slider__title">
            {{ projectName }}
          </p>
        </div>
      </client-only>

      <div class="project-slider__blackout"></div>
    </div>
  </div>

  <teleport to="body">
    <transition name="fade">
      <div
        v-if="showSliderModal"
        class="slider-modal"
        @click.self="showSliderModal = false"
      >
        <div class="slider-modal__close" @click="showSliderModal = false">
          <svgo-close class="icon" />
        </div>
        <div class="slider-modal__content">
          <swiper-container
            ref="swiper-modal"
            class="swiper-modal"
            :slides-per-view="1"
            :init="false"
          >
            <swiper-slide
              v-for="(slide, index) in filtredCovers"
              :key="index"
              class="swiper-slide slider-modal__slide"
            >
              <VideoComponent
                v-if="slide.type === 'video'"
                :play="currentIndexInModal === index && showSliderModal"
                :muted="muteVideo && !showSliderModal"
                :poster="slide.videoPart?.preview"
                :src="slide.data"
                controls
              />
              <SafeImage
                v-else
                :src="slide.data"
                loading="lazy"
                fetchpriority="low"
                :alt="`Slide ${index + 1}`"
              />
            </swiper-slide>
          </swiper-container>

          <template v-if="filtredCovers.length > 1">
            <div
              class="button-prev"
              :class="{ '--disabled': currentIndexInModal === 0 }"
              @click="goPrevInModal"
            >
              <svgo-arrow-left width="24" height="24" class="icon" />
            </div>
            <div
              class="button-next"
              :class="{
                '--disabled': currentIndexInModal === filtredCovers.length - 1,
              }"
              @click="goNextInModal"
            >
              <svgo-arrow-right width="24" height="24" class="icon" />
            </div>
          </template>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import projectDefaultCover from '~/assets/images/project/projectDefaultCover.jpg';

const VideoComponent = defineAsyncComponent(() =>
  import('~/components/VideoComponent.vue')
);

const { covers: projectCovers } = defineProps({
  covers: {
    type: Array,
    default: () => [],
  },
  projectName: {
    type: String,
    default: null,
  },
  finished: {
    type: Boolean,
    default: false,
  },
});

const isMobile = inject('isMobile');

const swiperRef = useTemplateRef('swiper');
const swiperModalRef = useTemplateRef('swiper-modal');

onMounted(() => {
  if (swiperRef.value) {
    swiperRef.value.initialize();
    swiperRef.value?.swiper?.on('slideChange', () => {
      currentIndex.value = swiperRef.value?.swiper?.activeIndex;
    });
  }
});

const muteVideo = ref(true);
const currentIndex = ref(0);
const currentIndexInModal = ref(0);
const showSliderModal = ref(false);

const filtredCovers = computed(() => {
  if (!projectCovers?.length) {
    return [
      {
        data: projectDefaultCover,
        type: 'image',
      },
    ];
  }

  return projectCovers.filter(
    (cover) => cover.type === 'image' || cover.type === 'video'
  );
});

watchEffect(() => {
  const firstImage = filtredCovers.value?.[0];

  if (firstImage?.type === 'image' && firstImage.imagePart?.preview) {
    useHead({
      link: [
        {
          rel: 'preload',
          as: 'image',
          href: firstImage.imagePart.preview,
        },
      ],
    });
  }
});

watch(showSliderModal, async (open) => {
  if (open) {
    disableScroll();
    await nextTick();

    const modalEl = swiperModalRef.value;

    if (!modalEl?.swiper) {
      modalEl.setAttribute('pagination', '');
      await modalEl.initialize();
    }

    modalEl.swiper?.slideTo(currentIndex.value, 0);
    currentIndexInModal.value = modalEl.swiper?.activeIndex;

    modalEl.swiper?.on('slideChange', () => {
      currentIndexInModal.value = modalEl.swiper?.activeIndex;
    });
  } else {
    enableScroll();
  }
});

function goPrev() {
  swiperRef.value?.swiper?.slidePrev();
}

function goPrevInModal() {
  swiperModalRef.value?.swiper?.slidePrev();
}

function goNext() {
  swiperRef.value?.swiper?.slideNext();
}

function goNextInModal() {
  swiperModalRef.value?.swiper?.slideNext();
}

function disableScroll() {
  const body = document.querySelector('body');
  body.dataset.scroll = scrollY;

  const bodyDefaultWidth = document.body.clientWidth;

  body.style.cssText = `
      top: -${document.body.dataset.scroll}px;
      position: fixed;
      width: 100%;
  `;

  const bodyDisableWidth = document.body.clientWidth;
  const padding = bodyDisableWidth - bodyDefaultWidth;
  body.style.paddingRight = padding + 'px';
}

function enableScroll() {
  const body = document.querySelector('body');
  body.removeAttribute('style');

  window.scroll({
    top: body.dataset.scroll,
  });

  body.removeAttribute('data-scroll');
}
</script>

<style lang="scss">
@use '@styles/components/pages/project/slider.scss' as *;
@use '@styles/components/pages/project/modalSlider.scss' as *;
</style>
